import { Component } from 'shimmer'

import {
  SkeletonHelper,
  AnimationMixer,
  AnimationUtils,
  LoopRepeat,
  LoopOnce,
  Vector3
} from 'three'

import gsap from 'gsap'

const additiveActions = {
  sneak_pose: { weight: 0 },
  sad_pose: { weight: 0 },
  agree: { weight: 0 },
  headShake: { weight: 0 }
}

// ANIMATION JSON ARGS
// isDefault: is setup as base animation or no animation animation
// repetitions: number of time played. if -1, play infinitely
// waitForEnd: if wait till animation ended before switching to next animation
// smoothEnd: does another play of the animation during transition to next animation
// endPos: the position of the obj at the end of the animation to compensate displacement

export class AvatarAnimator extends Component {
  constructor (onBaseActions, onAdditiveActions) {
    super('Avatar')

    this.onBaseActions = onBaseActions
    this.onAdditiveActions = onAdditiveActions

    this.crossFade = this.crossFade.bind(this)
    this._onObjectReady = this._onObjectReady.bind(this)
    this.setBaseAction = this.setBaseAction.bind(this)
    this.prepareAnimation = this.prepareAnimation.bind(this)

    this.baseActions = []
    this.additiveActions = []
    this.data = {}
    this.repetition = -1
    this.lastActionName = ''

    this._indexAction = 0

    this.onFinishAnimation = ()=>{}
  }

  _onObjectReady() {
    this.mixer = new AnimationMixer(this.obj)

    this.mixer.addEventListener( 'loop', () => {
      requestAnimationFrame(() => {
        this.onFinishAnimation()
      })
      if (!this.nextAction) {
        this.setBaseAction(this.isTalking ? this.defaultTalkingAction : this.defaultAction)
      }
    })

    this.mixer.addEventListener( 'finished', () => {
      requestAnimationFrame(() => {
        this.onFinishAnimation()
      })
      if (!this.nextAction) {
        this.setBaseAction(this.isTalking ? this.defaultTalkingAction : this.defaultAction)
      }
    })
  }

  prepareAnimation (obj, data) {
    this.data = {...this.data, ...data}

    obj.animations.forEach(animation => {
      const name = animation.name

      if ( this.data[ name ] ) {
        const action = this.mixer.clipAction( animation )
        action.clampWhenFinished = true
        action.zeroSlopeAtEnd = false
        action.weight = 0

        if (this.data[name].isDefault) {
          action.play()
          this.prepareAction(action)
          this.currentAction = action

          this.setBaseAction(name)
          this.defaultAction = name
        }

        if (this.data[name].isDefaultTalking) {
          this.defaultTalkingAction = name
        }
        if (this.data[name].repetitions === -1) {
          action.loop = LoopRepeat
          action.repetitions = Infinity
        }
        else if (this.data[name].repetitions === 1) {
          action.loop = LoopOnce
          action.repetitions = this.data[name].repetitions
        }

        this.baseActions.push( action )
      }
      else if ( additiveActions[ name ] ) {
        // Make the clip additive and remove the reference frame
        AnimationUtils.makeClipAdditive( animation )

        let clip = animation
        if ( animation.name.endsWith( '_pose' ) ) {
          clip = AnimationUtils.subclip( animation, animation.name, 2, 3, 30 )
        }
        const action = this.mixer.clipAction( clip )
        action.weight = additiveActions[name].weight
        action.play()
        this.additiveActions.push( action )
      }
    })

    this.onBaseActions(this.baseActions.map(action => {
      return action.getClip().name
    }))
  }

  // AS AN ABSTRACT CLASS, IT IS DEFINED
  // IF NO OTHER ONUPDATE IS
  onUpdate (time) {
    this._onUpdate(time)
  }

  _onUpdate ({ delta }) {
    if (!this.mixer) {return}

    this.mixer.update( delta * .001 )

    if (this.basePos && this.data[this.lastActionName] && this.data[this.lastActionName].endPos) {
      const endPos = this.data[this.lastActionName].endPos
      const progress = this.lastAction.getEffectiveWeight()

      this.position.x = this.basePos.x - endPos.x * (1-progress)
      this.position.y = this.basePos.y - endPos.y * (1-progress)
      this.position.z = this.basePos.z - endPos.z * (1-progress)
    }
  }

  setBaseAction (name) {
    if (name === this.currentAction.getClip().name) {return}

    const index = this.baseActions.findIndex((action, i) => {
      const clip = action.getClip()
      if (clip.name === name) {
        return true
      }
      return false
    })

    if (index === -1) {
      return
    }

    // IF NEXT ACTION IS PLANNED / DEFINED
    // NEW ACTION REPLACE IT
    if (this.nextAction) {
      this.nextAction = this.baseActions[index]
    }
    // IF NO NEXT ACTION PLANNED / DEFINED
    // PREPARE TRANSITION
    else {
      this.nextAction = this.baseActions[index]
      const currentName = this.currentAction.getClip().name
      if (this.data[currentName].waitForEnd) {
        this.onFinishAnimation = () => {
          // IS CROSSFADE IS DEFINED IN ANIMATION
          // USE IT FOR ENTER AND LEAVING ANIMATION
          // IF UNDEFINED -> 1
          const crossFade = this.data[name].crossFade ? this.data[name].crossFade : this.data[currentName].crossFade
          this.crossFade(crossFade)

          this.onFinishAnimation = () => {}
        }
      }
      else if (this.data[name] && this.data[currentName].repetitions === -1) {
        this.crossFade(this.data[name].crossFade)
      }
      else {
        // IF ACTION IS FINISHED AND IS NOT RUNNING
        // IF ACTION IS RUNNING IS IS AUTOMATICCALY SMOOTHED BY RUNNING
        this.crossFade(this.data[name].crossFade, this.data[currentName].smoothEnd)
      }
    }
  }

  crossFade (duration = 1, smooth) {
    // SMOOTHING END OF ACTION IS LUNCHING IT ONCE AGAIN
    // AND TRANSITION TO THE NEW ONE
    if (smooth && !this.currentAction.isRunning()) {
      this.prepareAction(this.currentAction)
    }

    this.prepareAction(this.nextAction)
    this.currentAction.crossFadeTo( this.nextAction, duration, true )

    return new Promise(resolve => {
      this.lastAction = this.currentAction // action leaving
      this.currentAction = this.nextAction // action entering
      this.nextAction = null

      // APPLY LAST POSITION BEFORE OVERWRITE LASTACTIONNAME
      if (this.lastActionName !== '' && this.data[this.lastActionName].endPos) {
        this.position.x = this.basePos.x - this.data[this.lastActionName].endPos.x
        this.position.y = this.basePos.y - this.data[this.lastActionName].endPos.y
        this.position.z = this.basePos.z - this.data[this.lastActionName].endPos.z
        // ERASE BASEPOS AT THE END OF TRANSITION TO PREVENT ONUPATE CALCULATION
        this.basePos = null
      }
      this.lastActionName = this.lastAction.getClip().name // PREVENT FROM CHASING THE NAME IN ONUPDATE
      // IF END POS IS NEEDED
      // STOCK START POSITION TO DEAL WITH TRANSITION
      if (this.data[this.lastActionName] && this.data[this.lastActionName].endPos) {
        this.basePos = this.position.clone()
      }

      setTimeout(() => {
        resolve()
      }, duration * 1000)
    })
  }

  calculeTransition () {
    const lastActionName = this.lastAction.getClip().name

    if (this.data[lastActionName] && this.data[lastActionName].endPos) {
      return basePos = this.position.clone()
      delta = pos.x
      delta = this.position.x - pos.x
    }
  }

  prepareAction (action) {
    action.enabled = true
    action.setEffectiveTimeScale(1)
    action.setEffectiveWeight(1)
    action.reset()
    action.play()
  }
}